/* jshint esversion: 6, -W097 */
/* globals angular */

(function(angular) {
    'use strict';

    angular
        .module('enervee')
        .directive('productLandingPage', productLandingPageDirective);

    /**
     * This directive adds products specified in the URL (either directly or through a
     * reference to a configuration in the Settings Service) to the current scope
     * through the variable `ProductLandingPage.products`. If the page is configured
     * through the Settings Service, it will also provide access to a configuration
     * object (`ProductLandingPage.config`), containing all of the configuration defined
     * in Settings Service. There are also several helper functions provided, including a
     * method to add a product to the cart, and to travel to the comparison page.
     *
     * The URL must contain one of the following query param structures:
     *     * name - The name of the landing page defined in the Settings Service under
     *              `product_landing_pages`.
     *   or
     *     * category - The category code of the category the products belong to.
     *     * pid - The product ID of the product to display. There can be multiple
     *             pid parameters in the URL.
     *
     * If both are provided, the `name` configuration will override the `category/pid`
     * configuration.
     *
     * @example
     * <div product-landing-page>
     *     <div ng-repeat="product in ProductLandingPage.products track by product.id">
     *         <button ng-click="ProductLandingPage.addProductToCart(product.id)">
     *             {{ ProductLandingPage.config._internal_cta_button_text }}
     *         </button>
     *     </div>
     *     <button ng-click="ProductLandingPage.goToComparePage()">Compare All</button>
     * </div>
     */
    function productLandingPageDirective() {
        return {
            restrict: 'A',
            controllerAs: 'ProductLandingPage',
            controller: productLandingPageController,
        };
    }

    productLandingPageController.$inject = [
        '$log',
        '$state',
        '$location',
        'AdService',
        'UtilService',
        'AnalyticsService',
        'CartAnalyticsService',
        'ProductCompareService',
        'EnerveeCheckoutService',
        'ProductLandingPageService',
    ];
    function productLandingPageController(
        $log,
        $state,
        $location,
        AdService,
        UtilService,
        AnalyticsService,
        CartAnalyticsService,
        ProductCompareService,
        EnerveeCheckoutService,
        ProductLandingPageService,
    ) {
        const vm = this;
        const search = $location.search();

        /**
         * Public data
         */
        vm.addingProductToCart = {};
        vm.additionalProductData = {};
        vm.config = UtilService.getSiteSetting("product_landing_pages", {})[search.name] || {};
        vm.adsEnabled = AdService.adsEnabled;
        vm.fetchingProducts = false;
        vm.products = [];
        vm.featured = [];
        vm.category = search.category;
        vm.categoryId = undefined;

        /**
         * Public Methods
         */
        vm.addProductToCart = addProductToCart;
        vm.goToComparePage = goToComparePage;
        vm.productsAreComparable = productsAreComparable;
        vm.getTotalPrice = getTotalPrice;
        vm.getQuantity = getQuantity;

        processProductData();

        /**
         * Executes data retrieval and formatting operations.
         * Gets the product array from the Settings Service or executes function to create one from
         * the url parameters. Formats array for additional product data.
         * Retrieives data from endpoints and assembles relevant product array for use in the view.
         */
        function processProductData() {
            const productsConfig = getProductConfig(ProductLandingPageService.PRODUCTS);
            const featuredConfig = getProductConfig(ProductLandingPageService.FEATURED);
            const fullConfig = [...productsConfig, ...featuredConfig];
            const categoryProductMap = (
                fullConfig.reduce(ProductLandingPageService.addToCategoryMap, {})
            );
            // convert config array into object for use elsewhere
            vm.additionalProductData = createConfigObject(fullConfig);
            vm.fetchingProducts = true;
            ProductLandingPageService.fetchProductData(categoryProductMap)
                .then(productData => ProductLandingPageService.assembleProductData(...productData, fullConfig)
                    .sort(UtilService.sortBySource(
                        fullConfig.map(product => product.product_id), product => product.id))
                     )
                .then(products => {
                    vm.featured = createProductArray(products, featuredConfig);
                    vm.products = createProductArray(products, productsConfig);
                })
                .finally(() => {
                    setCategory();
                    vm.fetchingProducts = false;
                });
        }

        /**
         * Sets vm.category to first product's category_code
         * from additional product data object.
         * Also sets vm.categoryId from first product in the products or
         * featured products array.
         */
        function setCategory() {
            if (!vm.category) {
                for (let product in vm.additionalProductData) {
                    vm.category = vm.additionalProductData[product].category_code;
                    break;
                }
            }
            vm.categoryId = [...vm.products, vm.featured][0].category_id;
        }

        /**
         * Takes in config name to get config from site settings or create one from url.
         *
         * @param {String} configName - Name of the config we want to access.
         * @return {Array} Products with config data or empty array.
         */
        function getProductConfig(configName) {
            return (
                vm.config[configName] || ProductLandingPageService.productListFromURL(search, configName)
            );
        }

        /**
         * Creates an array of only products that are in the config array.
         *
         * @param {Object[]} products - Array of products objects.
         * @param {Object[]} config - Array of products config options.
         * @returns {Object[]} Array of products.
         */
        function createProductArray(products, config) {
            const productIds = config.reduce((acc, curr) => {
                acc[curr.product_id] = true;
                return acc;
            }, {});
            const productArray = [];
            products.forEach(product => {
                if (productIds[product.id]) {
                    productArray.push(product);
                }
            });
            return productArray;
        }
        /**
         * Converts the config array to an object.
         *
         * @param {Array} configArray - Products from Settings Service or url.
         * @returns {Object} Updated object.
         */
        function createConfigObject(configArray) {
            return configArray.reduce((map, config) => {
                map[config.product_id] = config;
                return map;
            }, {});
        }

        /**
         * Get the total price to be displayed using the total cost of the bundle
         *
         * @param {number} price - price per item.
         * @param {string} productId - The ID of the product to add to the cart.
         * @param {number} backupQuantity - Options quantity from CMS page.
         * @returns {number} Total price.
         */
        function getTotalPrice(price, productId, backupQuantity) {
            return price * getQuantity(productId, backupQuantity);
        }

        /**
         * Gets the total quantity using the additionalProductData or CMS quantity.
         * If neither exists, it just uses 1.
         *
         * @param {string} productId - The ID of the product to add to the cart.
         * @param {number} backupQuantity - Options quantity from CMS page.
         * @returns {number} Quantity.
         */
        function getQuantity(productId, backupQuantity) {
            const productData = vm.additionalProductData[productId];
            const quantity = (productData && productData.quantity) || backupQuantity || 1;
            return quantity;
        }

        /**
         * Adds the provided product ID to the cart for purchasing.
         *
         * @param {string} productId - The ID of the product to add to the cart.
         * @param {number} quantity - The quantity to be added to the cart.
         *
         * TODO: This kind of logic (and the button displayed that uses this) is duplicated in
         *       `add-to-cart-button`. We should find a way to make that button more customizable.
         */
        function addProductToCart(productId, quantity) {
            vm.addingProductToCart[productId] = true
            EnerveeCheckoutService.addProductToCart(productId, quantity).then(function success() {
                CartAnalyticsService.reportEvent('Cart Engaged', {
                    location: 'campaignLandingPage',
                    type_of_change: 'Add Product',
                });
            }, function error(error) {
                $log.error('Enervee Checkout Error: ', error);
            }).finally(() => delete vm.addingProductToCart[productId]);
        }

        /**
         * Calls service to report the event and navigate to the compare page.
         */
        function goToComparePage() {
            // if statement just in case the ng-if in the html gets removed
            if (!productsAreComparable()) {
                return;
            }
            const allProducts = [...vm.featured, ...vm.products];
            const params = {
                    category_code: vm.additionalProductData[allProducts[0].id].category_code,
            };
            ProductCompareService.goToComparePage(allProducts, params);
        }

        /**
         * Checks that every product that is available in the directive is comparable by checking
         * the category code for that product.
         * @return {boolean}
         */
        function productsAreComparable() {
            const allProducts = [...vm.featured, ...vm.products];
            return allProducts.map(product => vm.additionalProductData[product.id].category_code)
                .every((categoryCode, _, arr) => categoryCode === arr[0]);
        }
    }
})(angular);
